/**
 * TrimTimeline
 * Non-destructive trim UI for audio and video files.
 *
 * Audio: renders a Web Audio API waveform
 * Video: renders a frame strip extracted via canvas
 *
 * Props:
 *   file          File object
 *   type          "audio" | "video"
 *   duration      number  (seconds) — pass once known from media element
 *   start         number  trimStart state
 *   end           number  trimEnd   state
 *   onTrimChange  ({start, end}) => void
 *   mediaRef      ref to the <audio>/<video> element for playhead sync
 */

import { useEffect, useRef, useState, useCallback } from "react"

const MIN_TRIM_SEC = 0.5 // minimum region width

// ─── Waveform (audio) ────────────────────────────────────────────────────────
function useWaveform(file) {
  const [peaks, setPeaks] = useState(null)

  useEffect(() => {
    if (!file || !file.type.startsWith("audio/")) return
    let cancelled = false
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)()
        const buffer = await ctx.decodeAudioData(e.target.result)
        const data = buffer.getChannelData(0)
        const BARS = 200
        const blockSize = Math.floor(data.length / BARS)
        const out = []
        for (let i = 0; i < BARS; i++) {
          let max = 0
          for (let j = 0; j < blockSize; j++) {
            const v = Math.abs(data[i * blockSize + j])
            if (v > max) max = v
          }
          out.push(max)
        }
        // Normalize to [0,1]
        const maxVal = Math.max(...out, 0.001)
        if (!cancelled) setPeaks(out.map((v) => v / maxVal))
        ctx.close()
      } catch {
        if (!cancelled) setPeaks([])
      }
    }
    reader.readAsArrayBuffer(file)
    return () => { cancelled = true }
  }, [file])

  return peaks
}

// ─── Frame strip (video) ─────────────────────────────────────────────────────
function useFrameStrip(file, duration, count = 12) {
  const [frames, setFrames] = useState(null)

  useEffect(() => {
    if (!file || !file.type.startsWith("video/") || !duration) return
    let cancelled = false
    const url = URL.createObjectURL(file)
    const video = document.createElement("video")
    video.src = url
    video.muted = true
    video.preload = "auto"

    const captured = []
    let idx = 0

    const captureNext = () => {
      if (cancelled || idx >= count) {
        URL.revokeObjectURL(url)
        if (!cancelled) setFrames(captured)
        return
      }
      const t = (idx / (count - 1)) * duration
      video.currentTime = t
    }

    video.onseeked = () => {
      const canvas = document.createElement("canvas")
      canvas.width = 80
      canvas.height = 45
      canvas.getContext("2d").drawImage(video, 0, 0, 80, 45)
      captured.push(canvas.toDataURL("image/jpeg", 0.6))
      idx++
      captureNext()
    }

    video.onloadedmetadata = () => captureNext()
    return () => {
      cancelled = true
      URL.revokeObjectURL(url)
    }
  }, [file, duration, count])

  return frames
}

// ─── TrimTimeline ─────────────────────────────────────────────────────────────
export default function TrimTimeline({ file, type, duration, start, end, onTrimChange, onSeek, mediaRef }) {
  const trackRef = useRef(null)
  const dragRef = useRef(null) // { handle: "left"|"right"|"region", startX, origStart, origEnd }
  const [playhead, setPlayhead] = useState(0)
  const rafRef = useRef(null)

  const peaks = useWaveform(type === "audio" ? file : null)
  const frames = useFrameStrip(type === "video" ? file : null, duration)

  // Sync playhead from media element
  useEffect(() => {
    const el = mediaRef?.current
    if (!el || !duration) return
    const tick = () => {
      setPlayhead(el.currentTime / duration)
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [mediaRef, duration])

  const toRatio = useCallback((clientX) => {
    const rect = trackRef.current.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  }, [])

  const toSec = useCallback((ratio) => ratio * duration, [duration])

  const onPointerDown = useCallback((e, handle) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    dragRef.current = {
      handle,
      startX: e.clientX,
      origStart: start,
      origEnd: end,
    }
  }, [start, end])

  const onPointerMove = useCallback((e) => {
    const d = dragRef.current
    if (!d || !duration) return
    const dx = e.clientX - d.startX
    const rect = trackRef.current.getBoundingClientRect()
    const dSec = (dx / rect.width) * duration

    if (d.handle === "left") {
      const ns = Math.max(0, Math.min(d.origStart + dSec, d.origEnd - MIN_TRIM_SEC))
      onTrimChange({ start: ns, end: end })
      onSeek?.(ns)
    } else if (d.handle === "right") {
      const ne = Math.min(duration, Math.max(d.origEnd + dSec, d.origStart + MIN_TRIM_SEC))
      onTrimChange({ start: start, end: ne })
      onSeek?.(ne)
    } else if (d.handle === "region") {
      const regionLen = d.origEnd - d.origStart
      let ns = d.origStart + dSec
      ns = Math.max(0, Math.min(ns, duration - regionLen))
      onTrimChange({ start: ns, end: ns + regionLen })
      onSeek?.(ns)
    }
  }, [duration, start, end, onTrimChange])

  const onPointerUp = useCallback(() => { dragRef.current = null }, [])

  if (!duration) return null

  const leftPct = (start / duration) * 100
  const widthPct = ((end - start) / duration) * 100

  const fmtTime = (s) => {
    if (!s && s !== 0) return ""
    const m = Math.floor(s / 60)
    const sec = (s % 60).toFixed(1).padStart(4, "0")
    return `${m}:${sec}`
  }

  return (
    <div className="flex flex-col gap-1.5 select-none">
      {/* Time labels */}
      <div className="flex justify-between text-[10px] text-admin_text_secondary tabular-nums px-1">
        <span>0:00</span>
        <span className="text-admin_primary font-medium">
          {fmtTime(start)} → {fmtTime(end)} ({fmtTime(end - start)})
        </span>
        <span>{fmtTime(duration)}</span>
      </div>

      {/* Track */}
      <div
        ref={trackRef}
        className="relative h-16 rounded-lg overflow-hidden bg-admin_neutral cursor-default outline-none"
        tabIndex={0}
        onPointerDown={(e) => { if (!dragRef.current) e.currentTarget.focus() }}
        onKeyDown={(e) => {
          if (e.code === "Space") {
            e.preventDefault()
            const el = mediaRef?.current
            if (!el) return
            el.paused ? el.play() : el.pause()
          }
        }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onClick={(e) => {
          // Only act on clicks directly on the track (not bubbled from handles/region)
          if (dragRef.current) return
          const ratio = toRatio(e.clientX)
          const t = toSec(ratio)
          onSeek?.(t)
          const el = mediaRef?.current
          if (el) el.currentTime = t
        }}
      >
        {/* Waveform — audio */}
        {type === "audio" && peaks && (
          <div className="absolute inset-0 flex items-end gap-px px-0.5 pb-0.5">
            {peaks.map((v, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-admin_primary/30"
                style={{ height: `${Math.max(4, v * 100)}%` }}
              />
            ))}
          </div>
        )}

        {/* Frame strip — video */}
        {type === "video" && frames && (
          <div className="absolute inset-0 flex">
            {frames.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                draggable={false}
                className="flex-1 h-full object-cover"
              />
            ))}
          </div>
        )}

        {/* Loading placeholders */}
        {type === "audio" && !peaks && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-admin_text_secondary">Loading waveform…</span>
          </div>
        )}
        {type === "video" && !frames && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-admin_text_secondary">Loading frames…</span>
          </div>
        )}

        {/* Darkened zones outside selection */}
        <div className="absolute inset-y-0 left-0 bg-black/50 pointer-events-none" style={{ width: `${leftPct}%` }} />
        <div className="absolute inset-y-0 right-0 bg-black/50 pointer-events-none" style={{ width: `${100 - leftPct - widthPct}%` }} />

        {/* Selected region border */}
        <div
          className="absolute inset-y-0 border-2 border-white/80 cursor-move"
          style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
          onPointerDown={(e) => onPointerDown(e, "region")}
        />

        {/* Left handle */}
        <div
          className="absolute inset-y-0 w-3 bg-white rounded-l cursor-ew-resize flex items-center justify-center z-10"
          style={{ left: `calc(${leftPct}% - 6px)` }}
          onPointerDown={(e) => onPointerDown(e, "left")}
        >
          <div className="w-0.5 h-5 bg-black/40 rounded-full" />
        </div>

        {/* Right handle */}
        <div
          className="absolute inset-y-0 w-3 bg-white rounded-r cursor-ew-resize flex items-center justify-center z-10"
          style={{ left: `calc(${leftPct + widthPct}% - 6px)` }}
          onPointerDown={(e) => onPointerDown(e, "right")}
        >
          <div className="w-0.5 h-5 bg-black/40 rounded-full" />
        </div>

        {/* Playhead */}
        <div
          className="absolute inset-y-0 w-px bg-yellow-400 pointer-events-none z-20"
          style={{ left: `${playhead * 100}%` }}
        />
      </div>

      <p className="text-[10px] text-admin_text_secondary text-center">
        Drag handles or the selected region · trim applied at upload
      </p>
    </div>
  )
}
